import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import R from 'ramda';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate } from '@fortawesome/free-solid-svg-icons/faRotate';

import Box from 'react-bulma-companion/lib/Box';
import Icon from 'react-bulma-companion/lib/Icon';
import Title from 'react-bulma-companion/lib/Title';
import Columns from 'react-bulma-companion/lib/Columns';
import Column from 'react-bulma-companion/lib/Column';
import Button from 'react-bulma-companion/lib/Button';
import Image from 'react-bulma-companion/lib/Image';
import Field from 'react-bulma-companion/lib/Field';
import Control from 'react-bulma-companion/lib/Control';
import Textarea from 'react-bulma-companion/lib/Textarea';
import Label from 'react-bulma-companion/lib/Label';
import Help from 'react-bulma-companion/lib/Help';
import Input from 'react-bulma-companion/lib/Input';

import { validateName, validateMobileNumber } from '_utils/validation';
import { attemptGetUser, attemptUpdateUser } from '_store/thunks/user';

export default function GeneralProfile() {
  const dispatch = useDispatch();
  const { user } = useSelector(R.pick(['user']));

  const [firstName, setFirstName] = useState(user.firstName || '');
  const [lastName, setLastName] = useState(user.lastName || '');
  const [bio, setBio] = useState(user.bio || '');
  const [brandName, setBrandName] = useState(user.brandName || '');
  const [mobileNumber, setMobileNumber] = useState(user.mobileNumber || '');
  const [email, setEmail] = useState(user.email || '');
  const [zammit, setZammit] = useState(user.zammit_token || '');
  const [profilePic, setProfilePic] = useState(user.profilePic || '');
  const [firstNameEdited, setFirstNameEdited] = useState(false);
  const [lastNameEdited, setLastNameEdited] = useState(false);
  const [bioEdited, setBioEdited] = useState(false);
  const [emailEdited, setEmailEdited] = useState(false);
  const [zammitEdited, setZammitEdited] = useState(false);
  const [brandNameEdited, setBrandNameEdited] = useState(false);
  const [mobileNumberEdited, setMobileNumberEdited] = useState(false);
  const [profilePicEdited, setProfilePicEdited] = useState(false);

  const resetState = useCallback(() => {
    setFirstName(user.firstName || '');
    setLastName(user.lastName || '');
    setBio(user.bio || '');
    setBrandName(user.brandName || '');
    setMobileNumber(user.mobileNumber || '');
    setProfilePic(user.profilePic || '');
    setFirstNameEdited(false);
    setLastNameEdited(false);
    setBioEdited(false);
    setBrandNameEdited(false);
    setMobileNumberEdited(false);
    setProfilePicEdited(false);
  }, [user]);

  useEffect(() => {
    resetState();
  }, [user, resetState]);

  const updateFirstName = e => {
    if (validateName(e.target.value)) {
      setFirstName(e.target.value);
      setFirstNameEdited(true);
    }
  };

  const updateLastName = e => {
    if (validateName(e.target.value)) {
      setLastName(e.target.value);
      setLastNameEdited(true);
    }
  };

  const updateBrandName = e => {
    if (validateName(e.target.value)) {
      setBrandName(e.target.value);
      setBrandNameEdited(true);
    }
  };

  const updateMobileNumber = e => {
    if (validateMobileNumber(e.target.value)) {
      setMobileNumber(e.target.value);
      setMobileNumberEdited(true);
    }
  };

  const updateEmail = e => {
      setEmail(e.target.value);
      setEmailEdited(true);
  };

  const updateZammit = e => {
    setZammit(e.target.value);
    setZammitEdited(true);
  };

  const updateBio = e => {
    setBio(e.target.value);
    setBioEdited(true);
  };

  const updateProfilePic = e => {
    setProfilePic(e.target.value);
    setProfilePicEdited(true);
  };

  const refresh = () => dispatch(attemptGetUser())
    .then(resetState)
    .catch(R.identity);

  const save = () => {
    const updatedUser = {};

    if (firstNameEdited) { updatedUser.first_name = firstName; }
    if (lastNameEdited) { updatedUser.last_name = lastName; }
    if (profilePicEdited) { updatedUser.profile_pic = profilePic; }
    if (bioEdited) { updatedUser.bio = bio; }
    if (brandNameEdited) { updatedUser.brand_name = brandName; }
    if (mobileNumberEdited) { updatedUser.mobile_number = mobileNumber; }
    if (emailEdited) { updatedUser.email = email; }
    if (zammitEdited) { updatedUser.zammit = zammit; }

    console.log(updatedUser);

    if (!R.isEmpty(updatedUser)) {
      dispatch(attemptUpdateUser(updatedUser))
        .catch(R.identity);
    }
  };

  const charactersRemaining = 240 - bio.length;
  const edited = firstNameEdited || lastNameEdited || bioEdited || profilePicEdited || brandNameEdited || mobileNumberEdited;

  return (
    <Box className="general-profile">
      <Icon size="medium" pull="right" onClick={refresh} onKeyPress={refresh}>
        <FontAwesomeIcon icon={faRotate} size="lg" />
      </Icon>
      <Title size="3">
        General
      </Title>
      <hr className="separator" />
      <Columns>
        <Column size="4">
          <Title size="3" textAlign="center">
            {user.usernameCase}
          </Title>
          <Image>
            <Image.Content
              className="profile-img"
              src={profilePic || '/images/default-profile.png'}
              alt="Profile"
            />
          </Image>
          <Field>
            <Label htmlFor="profile-pic-url">
              Picture URL
            </Label>
            <Control>
              <Input
                id="profile-pic-url"
                placeholder="Picture URL"
                value={profilePic}
                onChange={updateProfilePic}
              />
            </Control>
          </Field>
        </Column>
        <Column size="8">
          <Columns>
            <Column size="6">
              <Field>
                <Label htmlFor="first-name" className="Label">
                  First Name
                </Label>
                <Control>
                  <Input
                    id="first-name"
                    placeholder="First Name"
                    value={firstName}
                    onChange={updateFirstName}
                  />
                </Control>
              </Field>
            </Column>
            <Column size="6">
              <Field>
                <Label htmlFor="last-name">
                  Last Name
                </Label>
                <Control>
                  <Input
                    id="last-name"
                    placeholder="Last Name"
                    value={lastName}
                    onChange={updateLastName}
                  />
                </Control>
              </Field>
            </Column>
          </Columns>
          <Columns>
            <Column size="6">
              <Field>
                <Label htmlFor="brand-name">
                  Brand Name
                </Label>
                <Control>
                  <Input
                    id="brand-name"
                    placeholder="Brand Name"
                    value={brandName}
                    onChange={updateBrandName}
                  />
                </Control>
              </Field>
            </Column>
            <Column size="6">
              <Field>
                <Label htmlFor="mobile-number">
                  Mobile Number
                </Label>
                <Control>
                  <Input
                    id="mobile-number"
                    placeholder="Mobile Number"
                    value={mobileNumber}
                    onChange={updateMobileNumber}
                  />
                </Control>
              </Field>
            </Column>
          </Columns>
          <Columns>
            <Column size="12">
              <Field>
                <Label htmlFor="email">
                  Email
                </Label>
                <Control>
                  <Input
                    id="email"
                    placeholder="Email"
                    value={email}
                    onChange={updateEmail}
                  />
                </Control>
              </Field>
            </Column>
          </Columns>
          <Columns>
            <Column size="12">
              <Field>
                <Label htmlFor="zammit">
                  Zammit Token
                </Label>
                <Control>
                  <Input
                    id="zammit"
                    placeholder="Zammit Token"
                    value={zammit}
                    onChange={updateZammit}
                  />
                </Control>
              </Field>
            </Column>
          </Columns>
          <Field>
            <Label htmlFor="bio">
              Bio
            </Label>
            <Control>
              <Textarea
                id="bio"
                placeholder="Tell us about yourself."
                value={bio}
                maxLength={240}
                onChange={updateBio}
              />
            </Control>
            <Help>
              {`Characters remaining: ${charactersRemaining}`}
            </Help>
          </Field>
        </Column>
      </Columns>
      <hr className="separator" />
      <Button color="success" onClick={save} disabled={!edited}>
        Save
      </Button>
    </Box>
  );
}
